import { useState } from "react";
import { RefreshCw, Loader2 } from "lucide-react";
import usePartners from "src/hooks/usePartners";
import { useSyncToPartners, isProductSyncSupported, formatPartnerNames, executePartnerSync } from "src/hooks/useSyncToPartners";
import Tooltip from "src/components/core/Tooltip";
import { Listing, EnumProductTypes } from "src/types/listing";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "src/components/shadcn/DropDownMenu";
import { partnerIcons } from "src/utils/partnerIcons";

// Transform listing ID to the appropriate format for the API
// NS uses string UUIDs, other products use numeric IDs
const getRecordId = (productType: string, id: string): number | string => {
  return productType === EnumProductTypes.ns ? id : Number(id);
};

interface Partner {
  id: string;
  name: string;
  enabled: boolean;
  isRollout?: boolean;
}

interface PartnerSyncButtonsProps {
  listing: Listing;
  variant?: "buttons" | "menu" | "auto"; // "auto" uses menu when 2+ partners
}

export function PartnerSyncButtons({ listing, variant = "auto" }: PartnerSyncButtonsProps) {
  const { data: partners = [] } = usePartners();
  const { mutate: syncToPartners, isPending } = useSyncToPartners();
  const [syncingPartnerId, setSyncingPartnerId] = useState<string | null>(null);

  // Check if manual sync feature is enabled via environment variable
  const isManualSyncEnabled = process.env.REACT_APP_ENABLE_MANUAL_PARTNER_SYNC === "true";

  if (!isManualSyncEnabled) {
    return null;
  }

  // Only show for supported products
  if (!isProductSyncSupported(listing.productType || "")) {
    return null;
  }

  // Only enabled partners and those that support a single manual push (currently only rollout)
  const manualSyncPartners = partners.filter((p: Partner) => p.enabled && p.isRollout);

  // Don't render if no manual sync partners available
  if (manualSyncPartners.length === 0) {
    return null;
  }

  const handleSync = (partnerId?: string) => {
    const { partnerName } = formatPartnerNames(manualSyncPartners, partnerId);

    setSyncingPartnerId(partnerId || "all");

    const recordId = getRecordId(listing.productType || "", listing.id);

    executePartnerSync(syncToPartners, {
      product: listing.productType || "",
      recordIds: [recordId],
      partnerId,
      partnerName,
      onSettled: () => setSyncingPartnerId(null),
    });
  };

  const isButtonDisabled = isPending;

  // Determine effective variant: auto uses menu when 2+ partners
  const effectiveVariant = variant === "auto"
    ? manualSyncPartners.length >= 2 ? "menu" : "buttons"
    : variant;

  // Menu variant - single button that opens dropdown
  if (effectiveVariant === "menu") {
    return (
      <DropdownMenu modal={false}>
        <Tooltip content="Sync to partners">
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="w-6 h-6 flex items-center justify-center cursor-pointer hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-primary"
              disabled={isButtonDisabled}
            >
              {isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5" />
              )}
            </button>
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
          {manualSyncPartners.map((partner: Partner) => {
            const icon = partnerIcons[partner.id];
            const isThisSyncing = syncingPartnerId === partner.id;

            return (
              <DropdownMenuItem
                key={partner.id}
                className="group gap-3 cursor-pointer"
                disabled={isButtonDisabled}
                onClick={() => handleSync(partner.id)}
              >
                {isThisSyncing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : icon ? (
                  <img src={icon} alt={partner.name} className="w-4 h-4 object-contain" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Sync to {partner.name}
              </DropdownMenuItem>
            );
          })}
          {manualSyncPartners.length > 1 && (
            <DropdownMenuItem
              className="gap-3 cursor-pointer"
              disabled={isButtonDisabled}
              onClick={() => handleSync()}
            >
              {syncingPartnerId === "all" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Sync to All Partners
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Buttons variant - individual icon buttons
  return (
    <>
      {manualSyncPartners.map((partner: Partner) => {
        const icon = partnerIcons[partner.id];
        const isThisSyncing = syncingPartnerId === partner.id;

        return (
          <Tooltip key={partner.id} content={`Sync to ${partner.name}`}>
            <button
              type="button"
              className="relative w-6 h-6 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleSync(partner.id)}
              disabled={isButtonDisabled}
            >
              {isThisSyncing ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-primary" />
              ) : icon ? (
                <img
                  src={icon}
                  alt={partner.name}
                  className="w-5 h-5 object-contain grayscale hover:grayscale-0 transition-all"
                />
              ) : (
                <span className="text-xs font-medium text-gray-primary">
                  {partner.name.charAt(0)}
                </span>
              )}
            </button>
          </Tooltip>
        );
      })}

      {/* Sync All button - only show if more than 1 partner enabled */}
      {manualSyncPartners.length > 1 && (
        <Tooltip content="Sync to all partners">
          <button
            type="button"
            className="w-6 h-6 flex items-center justify-center cursor-pointer hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-primary"
            onClick={() => handleSync()}
            disabled={isButtonDisabled}
          >
            {syncingPartnerId === "all" ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <RefreshCw className="w-5 h-5" />
            )}
          </button>
        </Tooltip>
      )}
    </>
  );
}
